interface AppConfig {
	app: {
		title: string
		favicon: string
		logo: string
		description: string
		version: string
		showMsg: boolean
	}
	theme: {
		primaryColor: string
		logoText: string
		sidebarStyle?: string
		headerStyle?: string
	}
	features: {
		aiAssistant: boolean
		performanceMonitor: boolean
		dcsEnabled?: boolean
	}
	login: {
		showPlatformSelection: boolean
		defaultPlatform: string
		platforms: {
			[key: string]: {
				name: string
				description: string
				route: string
				platValue: number
			}
		}
	}
	search?: {
		autoSearch: boolean
		debounceTime: number
	}
	mqtt: {
		url: string
	}
}

let appConfig: AppConfig | null = null

/**
 * 加载应用配置
 */
export async function loadAppConfig(forceReload: boolean = false): Promise<AppConfig> {
	if (appConfig && !forceReload) {
		return appConfig
	}

	try {
		// 添加时间戳避免浏览器缓存
		const timestamp = Date.now()
		const testPaths = (import.meta.env.VITE_TEST_PATHS || '').split(',').filter(Boolean)
		const isTestEnv = testPaths.some(path => window.location.href.includes(path))
		let response: Response
		if (isTestEnv) {
			response = await fetch(`/app-config-test.json?t=${timestamp}`)
		} else {
			response = await fetch(`/app-config.json?t=${timestamp}`)
		}
		if (!response.ok) {
			throw new Error(`Failed to load config: ${response.status}`)
		}
		appConfig = await response.json()
		return appConfig!
	} catch (error) {
		console.warn('Failed to load app config, using defaults:', error)
		// 返回默认配置
		appConfig = {
			app: {
				title: 'Maku Admin Enterprise',
				favicon: '/favicon.ico?timestamp=' + Date.now(),
				logo: '/logo.png?timestamp=' + Date.now(),
				description: '企业级管理系统',
				version: '1.0.0',
				showMsg: true
			},
			theme: {
				primaryColor: '#409EFF',
				logoText: 'Maku Admin'
			},
			features: {
				aiAssistant: true,
				performanceMonitor: true,
				dcsEnabled: true
			},
			search: {
				autoSearch: false,
				debounceTime: 2000
			},
			login: {
				showPlatformSelection: true,
				defaultPlatform: '',
				platforms: {
					intelligent: {
						name: '智能运维平台',
						description: '专家诊断、智能诊断、智能硬件',
						route: '/',
						platValue: 1
					},
					lifecycle: {
						name: '设备全生命周期',
						description: '前期管理、后期管理、运维管理',
						route: '/screen2',
						platValue: 2
					}
				}
			},
			mqtt: {
				url: 'ws://localhost:7084/mqtt'
			}
		}
		return appConfig
	}
}

/**
 * 获取当前应用配置
 */
export function getAppConfig(): AppConfig | null {
	return appConfig
}

/**
 * 获取 DCS 功能是否启用
 */
export function isDcsEnabled(): boolean {
	return appConfig?.features?.dcsEnabled ?? true
}

/**
 * 更新页面标题
 */
export function updatePageTitle(title: string) {
	const customTitle = localStorage.getItem('customTitle')
	if (customTitle) {
		document.title = customTitle
	} else {
		document.title = title
	}
}

/**
 * 获取搜索表单配置
 */
export function getSearchConfig() {
	return appConfig?.search ?? { autoSearch: true, debounceTime: 2000 }
}

/**
 * 更新页面图标
 */
export function updatePageFavicon(faviconUrl: string) {
	const testPaths = (import.meta.env.VITE_TEST_PATHS || '').split(',').filter(Boolean)
	const isTestEnv = testPaths.some(path => window.location.href.includes(path))
	if (isTestEnv) {
		let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
		if (!link) {
			link = document.createElement('link')
			link.rel = 'icon'
			document.head.appendChild(link)
		}
		link.href = '/favicon-test.ico' + '?t=' + Date.now()
		return
	}

	let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
	if (!link) {
		link = document.createElement('link')
		link.rel = 'icon'
		document.head.appendChild(link)
	}
	link.href = faviconUrl + '?t=' + Date.now()
}

import { useAppStore } from '@/store/modules/app'
import { handleThemeStyle } from '@/utils/theme'
import cache from '@/utils/cache'

/**
 * 初始化应用配置
 */
export async function initAppConfig() {
	const config = await loadAppConfig()

	// 更新页面标题
	updatePageTitle(config.app.title)

	// 更新页面图标
	updatePageFavicon(config.app.favicon)

	// 应用主题配置
	if (config.theme) {
		const appStore = useAppStore()
		let themeChanged = false

		if (config.theme.primaryColor && config.theme.primaryColor !== appStore.theme.primaryColor) {
			appStore.theme.primaryColor = config.theme.primaryColor
			themeChanged = true
		}
		if (config.theme.sidebarStyle && config.theme.sidebarStyle !== appStore.theme.sidebarStyle) {
			appStore.theme.sidebarStyle = config.theme.sidebarStyle
			themeChanged = true
		}
		if (config.theme.headerStyle && config.theme.headerStyle !== appStore.theme.headerStyle) {
			appStore.theme.headerStyle = config.theme.headerStyle
			themeChanged = true
		}

		if (themeChanged) {
			handleThemeStyle(appStore.theme)
			cache.setTheme(appStore.theme)
		}
	}

	console.log('App config initialized:', config)
	return config
}
